/**
 * FROZEN CONTRACT — base stats, IVs, EVs, natures, levels, and computed stats.
 */

/** The six battle stats, in canonical order. */
export type StatKey = 'hp' | 'atk' | 'def' | 'spatk' | 'spdef' | 'spe';

/** A full set of one value per stat. */
export type StatSpread<T = number> = Readonly<Record<StatKey, T>>;

/** A species' immutable base stats. */
export type BaseStats = StatSpread<number>;

/** Individual Values, 0–31 per stat (genetic perfection). */
export type IVs = StatSpread<number>;

/** Effort Values, 0–252 per stat, ≤510 total (training investment). */
export type EVs = StatSpread<number>;

/** Fully derived stats at the mon's current level. */
export type ComputedStats = StatSpread<number>;

export const STAT_KEYS = [
  'hp',
  'atk',
  'def',
  'spatk',
  'spdef',
  'spe',
] as const satisfies readonly StatKey[];

export const IV_MIN = 0;
export const IV_MAX = 31;
export const EV_MAX_PER_STAT = 252;
export const EV_MAX_TOTAL = 510;
export const LEVEL_MIN = 1;
export const LEVEL_MAX = 100;

/** The non-HP stats a nature can boost or reduce. */
export type NatureAffectedStat = Exclude<StatKey, 'hp'>;

/**
 * A nature applies +10% to one stat and -10% to another. When `raises === lowers`
 * (a "neutral" nature, e.g. Hardy), there is no net effect.
 */
export interface Nature {
  readonly id: string;
  readonly name: string;
  readonly raises: NatureAffectedStat;
  readonly lowers: NatureAffectedStat;
}

/**
 * Real stat formula the engine (core/stats) implements:
 *
 *   HP    = floor((2*base + iv + floor(ev/4)) * level / 100) + level + 10
 *   other = floor( (floor((2*base + iv + floor(ev/4)) * level / 100) + 5) * natureMod )
 *
 * where natureMod ∈ {0.9, 1.0, 1.1}. Shedinja (HP=1) is a documented exception.
 */
export type StatFormulaNote = never;
